import { useState, useMemo, useCallback, useRef, useEffect, type ReactNode, type DragEvent } from 'react'
import { File, Folder, FolderOpen, ChevronRight, ChevronDown, Plus, Upload, FilePlus, FolderPlus, ArrowRight, Edit3, Trash2, Files, Copy, X, ClipboardPaste, Search, Eye, EyeOff, AlertTriangle, History } from 'lucide-react'
import {
    COMPONENT_JOB_SOURCE_EXTENSIONS,
    createComponentJobScaffold,
    deriveComponentJobConversionPlan,
    getComponentJobCompanionPaths,
    getComponentJobRootPath,
    getComponentJobSourcePath,
    isBlankComponentJobSource,
    isComponentJobInternalPath,
    serializeComponentJobConfig,
    type ComponentJobStarterTemplate,
} from '@secureblob/sdk/componentJobs'
import { htmlToMarkdown, hasRichHtmlContent } from '../utils/htmlToMarkdown'
import { useFileSystem, useWorkspace } from '../hooks/useWorkspace'
import type { FileStat, Workspace, ImportFileEntry } from '../engine/types'
import { writerColor, timeAgo, formatExactTime } from '../utils/writerUtils'
import { detectFileExtension } from '../utils/detectFileExtension'
import { imageExtensionFromMimeType } from '../utils/imageExtension'
import { cascadeRename, collectDeleteTargets } from '../utils/cascadeFileOps'
import ContextMenu, { type MenuEntry, type MenuSection, type ContextTargetType } from './ContextMenu'
import {
    applyStarterScaffoldToJobRoot,
    createSourceCompatibleComponentJobScaffold,
    listComponentJobStarterScaffoldsForJobRoot,
    listComponentJobStarterScaffoldsForSourceFile,
    scaffoldNewComponentJobRoot,
} from './componentJobs/starterScaffolds'
import { resolveComponentJobSourcePath } from './componentJobs/jobHistory'
import { useToast } from './Toast'
import InlineTreeInput from './InlineTreeInput'
import './FileBrowser.css'

// ── Types ──────────────────────────────────────────────────────

interface TreeNode {
    name: string
    path: string
    children: Record<string, TreeNode>
    files: TreeFile[]
    isAgentFolder?: boolean
    isAttachmentFolder?: boolean
    isComponentJobFolder?: boolean
    parentFilePath?: string  // for attachment folders: the path of the parent .md file
}

interface TreeFile extends FileStat {
    name: string
    isRunResult?: boolean
}

interface CtxMenuState {
    x: number
    y: number
    targetType: ContextTargetType
    targetPath: string
}

// FileEntry interface removed — readEntryRecursive now returns ImportFileEntry
// directly with eagerly-read content (Safari file-backing recycling fix).

interface InlineInputState {
    mode: 'newFile' | 'newFolder' | 'newJob' | 'rename'
    parentPath: string
    currentName?: string   // for rename: pre-fill with current name
    targetPath?: string    // for rename: the full path being renamed
}

const INDENT_PX = 20      // pixels per depth level
const CHEVRON_SPACE = 22  // chevron (14px) + gap (8px) — offset so file icons align with folder icons
const COMPONENT_JOB_STARTER_ACTION_PREFIX = 'createComponentJobStarter:'
const EMPTY_DIR_SET: ReadonlySet<string> = new Set()
const EMPTY_DIR_ERRORS: Readonly<Record<string, string>> = {}

function normalizeBrowserDirPath(path: string): string {
    if (!path || path === '/') return '/'
    const normalized = path.replace(/\\/g, '/').replace(/\/+$/, '')
    return normalized.startsWith('/') ? normalized : `/${normalized}`
}

function collapseNestedMoveSelections(paths: Iterable<string>): string[] {
    const roots: string[] = []
    const sorted = [...paths].sort((a, b) => a.length - b.length || a.localeCompare(b, 'sv'))

    for (const path of sorted) {
        if (roots.some(root => path === root || path.startsWith(root + '/'))) continue
        roots.push(path)
    }

    return roots
}

function applyRenameOpsToSnapshot(files: FileStat[], renames: [string, string][]): FileStat[] {
    if (renames.length === 0) return files
    const renameMap = new Map(renames)
    return files.map(file => {
        const renamedPath = renameMap.get(file.path)
        return renamedPath ? { ...file, path: renamedPath } : file
    })
}

// ── Helpers ────────────────────────────────────────────────────

/**
 * Build a tree structure from flat file paths.
 */
function buildTree(files: FileStat[]): TreeNode {
    const root: TreeNode = { name: '', path: '/', children: {}, files: [] }

    // Sort files to handle folders/configs before transcript sessions
    const sorted = [...files].sort((a, b) => a.path.length - b.path.length)

    // Track which agent configs have transcript sessions (to avoid duplicate listing)
    const agentConfigsWithRuns = new Set<string>()
    const seenRuns = new Set<string>()  // Deduplicate: one entry per transcript folder

    for (const f of sorted) {
        // Special case: transcript files like /path/to/my.agent.json.transcripts/session-123/transcript.json
        if (/\.agent\.json\.transcripts\//.test(f.path)) {
            // Only surface completed transcript files as selectable entries.
            // Ignore the synthetic `.transcripts/` root dir and intermediate folders,
            // otherwise the tree can produce blank/invalid transcript items.
            if (!f.path.endsWith('/transcript.json')) continue

            // Derive config path: /path/to/my.agent.json.transcripts/... → /path/to/my.agent.json
            const agentDirMatch = f.path.match(/^(.*?\.agent\.json)\.transcripts\//)
            if (!agentDirMatch) continue
            const agentDir = agentDirMatch[1] + '.transcripts'  // e.g. /assistant.agent.json.transcripts
            const agentPath = agentDirMatch[1]  // e.g. /assistant.agent.json
            agentConfigsWithRuns.add(agentPath)

            // Extract transcript folder name for display (e.g. "session-1770667198917-1acg")
            const afterAgent = f.path.slice(agentDir.length + 1) // "session-xxx/transcript.json"
            const runFolderName = afterAgent.split('/')[0] // "session-1770667198917-1acg"

            // Deduplicate: only add one entry per run folder (use the transcript.json)
            const runKey = `${agentDir}/${runFolderName}`
            if (seenRuns.has(runKey)) continue
            seenRuns.add(runKey)

            // Format display name from timestamp in session ID (handles both session-xxx and session-emb-xxx)
            const tsMatch = runFolderName.match(/^session-(?:emb-)?(\d+)/)
            let displayName = runFolderName
            if (tsMatch) {
                const ts = parseInt(tsMatch[1], 10)
                const d = new Date(ts)
                displayName = d.toLocaleDateString('sv') + ' ' + d.toLocaleTimeString('sv', { hour: '2-digit', minute: '2-digit' })
            }

            // Navigate to the parent directory of the agent config
            const parts = agentPath.split('/').filter(Boolean)
            let node = root
            for (let i = 0; i < parts.length; i++) {
                if (i === parts.length - 1) {
                    // This is the agent.json file — create as virtual folder
                    if (!node.children[parts[i]]) {
                        node.children[parts[i]] = {
                            name: parts[i],
                            path: agentPath,
                            children: {},
                            files: [],
                            isAgentFolder: true
                        }
                    }
                    node = node.children[parts[i]]
                } else {
                    if (!node.children[parts[i]]) {
                        node.children[parts[i]] = { name: parts[i], path: '/' + parts.slice(0, i + 1).join('/'), children: {}, files: [] }
                    }
                    node = node.children[parts[i]]
                }
            }
            // Add transcript as a child file of the agent folder with the transcript path
            const runPath = `${agentDir}/${runFolderName}/transcript.json`
            node.files.push({ ...f, name: displayName, path: runPath, isRunResult: true })
            continue
        }

        const parts = f.path.split('/').filter(Boolean)
        let node = root
        let currentPath = ''
        for (let i = 0; i < parts.length - 1; i++) {
            currentPath += '/' + parts[i]
            if (!node.children[parts[i]]) {
                node.children[parts[i]] = { name: parts[i], path: currentPath, children: {}, files: [] }
            }
            node = node.children[parts[i]]
        }
        const fileName = parts[parts.length - 1]
        // Agent config files that have transcript sessions are rendered as their agent folder node
        if (f.path.endsWith('.agent.json') && agentConfigsWithRuns.has(f.path)) {
            // Ensure the agent folder node exists and is marked
            if (!node.children[fileName]) {
                node.children[fileName] = {
                    name: fileName,
                    path: f.path,
                    children: {},
                    files: [],
                    isAgentFolder: true
                }
            }
            continue
        }
        // Agent config files without runs also render as expandable agent folders
        if (f.path.endsWith('.agent.json')) {
            if (!node.children[fileName]) {
                node.children[fileName] = {
                    name: fileName,
                    path: f.path,
                    children: {},
                    files: [],
                    isAgentFolder: true
                }
            }
            continue
        }
        // Explicit directory entries (kind='directory') — ensure they appear as tree nodes
        if (f.kind === 'directory') {
            const dirName = parts[parts.length - 1]
            if (dirName && !node.children[dirName]) {
                const dirPath = '/' + parts.join('/')
                node.children[dirName] = { name: dirName, path: dirPath, children: {}, files: [] }
            }
            continue
        }
        node.files.push({ ...f, name: fileName })
    }

    // ── Attachment companion folders ─────────────────────────────
    // Convention: `foo.md.attachments/` is grouped under `foo.md`
    // Walk the tree and relocate attachment folders under their parent file.
    function groupAttachments(node: TreeNode) {
        for (const key of Object.keys(node.children)) {
            const child = node.children[key]
            // Recurse first so nested dirs are processed
            groupAttachments(child)

            // Check if this directory name matches `*.attachments`
            if (!key.endsWith('.attachments')) continue
            // Find the parent file (e.g. `readme.md` for `readme.md.attachments`)
            const parentFileName = key.replace(/\.attachments$/, '')
            const parentFile = node.files.find(f => f.name === parentFileName)
            if (!parentFile) continue

            // Convert the parent file into a virtual attachment folder node
            // that contains the original file + grouped attachment children
            const attachNode: TreeNode = {
                name: parentFileName,
                path: parentFile.path,
                children: {},
                files: child.files,
                isAttachmentFolder: true,
                parentFilePath: parentFile.path,
            }
            node.children[parentFileName] = attachNode
            // Remove the original file from sibling list (now rendered as the folder)
            node.files = node.files.filter(f => f.name !== parentFileName)
            // Remove original .attachments directory (now merged)
            delete node.children[key]
        }
    }
    groupAttachments(root)
    groupComponentJobs(root)
    return root

    function groupComponentJobs(node: TreeNode) {
        for (const key of Object.keys(node.children)) {
            groupComponentJobs(node.children[key])
        }

        for (const file of [...node.files]) {
            const folderFirstRoot = getComponentJobRootPath(file.path)
            if (folderFirstRoot && folderFirstRoot !== file.path) {
                continue
            }

            let companionPaths: ReturnType<typeof getComponentJobCompanionPaths>
            try {
                companionPaths = getComponentJobCompanionPaths(file.path)
            } catch {
                continue
            }

            const settingsFile = node.files.find(candidate => candidate.path === companionPaths.settings)
            const runsDir = node.children[`${file.name}.runs`]
            const buildsDir = node.children[`${file.name}.builds`]

            if (!settingsFile && !runsDir && !buildsDir) continue

            const componentNode: TreeNode = {
                name: file.name,
                path: file.path,
                children: {},
                files: [],
                isComponentJobFolder: true,
                parentFilePath: file.path,
            }

            if (settingsFile) {
                componentNode.files.push({ ...settingsFile, name: 'Settings' })
            }
            if (runsDir) {
                componentNode.children.Runs = { ...runsDir, name: 'Runs' }
                delete node.children[`${file.name}.runs`]
            }
            if (buildsDir) {
                componentNode.children.Builds = { ...buildsDir, name: 'Builds' }
                delete node.children[`${file.name}.builds`]
            }

            node.children[file.name] = componentNode
            node.files = node.files.filter(candidate => candidate.path !== file.path && candidate.path !== companionPaths.settings)
        }

        for (const child of Object.values(node.children)) {
            if (!child.parentFilePath && isFolderFirstComponentJobNode(child)) {
                child.isComponentJobFolder = true
            }
        }
    }
}

function isFolderFirstComponentJobNode(node: TreeNode): boolean {
    if (!node.path.endsWith('.job')) return false
    return node.files.some(file => file.name === 'job.json')
        || node.files.some(file => COMPONENT_JOB_SOURCE_EXTENSIONS.some(ext => file.name === `main${ext}`))
        || !!node.children['.secureblob']
}

function getFileIcon(name: string, isRunResult?: boolean): ReactNode {
    if (isRunResult) return <span className="icon-run" title="Chat Transcript">📄</span>
    if (name.endsWith('.agent.json')) return <span className="icon-agent" title="AI Agent">🤖</span>
    if (name.endsWith('.md')) return <File size={16} className="icon-md" />
    if (name.endsWith('.json')) return <File size={16} className="icon-json" />
    return <File size={16} />
}

function isFileBackedVirtualFolder(node: TreeNode): boolean {
    return node.isAgentFolder === true || node.isAttachmentFolder === true || node.isComponentJobFolder === true
}

function buildAncestorPaths(path: string): string[] {
    const normalized = path.replace(/\/$/, '')
    const parts = normalized.split('/').filter(Boolean)
    const ancestors = ['/']
    for (let i = 0; i < parts.length - 1; i += 1) {
        ancestors.push('/' + parts.slice(0, i + 1).join('/'))
    }
    return ancestors
}


function formatMeta(stat: TreeFile): string {
    const size = stat?.size ?? 0
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${Math.round(size / 102.4) / 10} KB`
    return `${Math.round(size / 1024 / 102.4) / 10} MB`
}

// ── Confirm Modal (in-app confirmation — replaces raw confirm()) ───────

interface ConfirmState {
    title: string
    message: string
    confirmLabel?: string
    onConfirm: () => void
}

function ConfirmModal({ state, onCancel }: { state: ConfirmState; onCancel: () => void }) {
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [onCancel])

    return (
        <>
            <div className="fb-confirm-backdrop" onClick={onCancel} />
            <div className="fb-confirm-modal" role="dialog" aria-label={state.title}>
                <div className="fb-confirm-body">
                    <h3><AlertTriangle size={18} className="fb-confirm-icon" /> {state.title}</h3>
                    {state.message.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                    ))}
                </div>
                <div className="fb-confirm-footer">
                    <button className="fb-confirm-cancel" onClick={onCancel}>Cancel</button>
                    <button className="fb-confirm-ok" onClick={state.onConfirm}>
                        {state.confirmLabel || 'OK'}
                    </button>
                </div>
            </div>
        </>
    )
}

/** Small attribution dot showing the last writer of a file. */
function WriterDot({ kid, mtime, workspace }: { kid: string; mtime?: number; workspace: Workspace }) {
    const resolved = workspace.resolveWriterName(kid)
    const displayName = resolved.name || kid.slice(0, 8)
    const emoji = resolved.emoji || '👤'
    const time = mtime ? timeAgo(mtime) : ''
    const exact = mtime ? formatExactTime(mtime) : ''
    const tip = exact ? `${emoji} ${displayName} · ${time}\n${exact}` : `${emoji} ${displayName}`
    return (
        <span
            className="writer-dot"
            style={{ background: writerColor(kid) }}
            title={tip}
        />
    )
}

/**
 * Read all files from a dropped directory entry (recursive).
 * Uses the File System Access API (webkitGetAsEntry).
 *
 * IMPORTANT: Content is read eagerly (file.arrayBuffer() called immediately)
 * to prevent Safari from recycling File backing storage before content is
 * consumed. Safari reuses temporary handles during mass drag-and-drop —
 * deferring arrayBuffer() to a later loop causes cross-file content mix-up.
 */
async function readEntryRecursive(entry: FileSystemEntry, basePath = '', counter = { n: 0 }): Promise<ImportFileEntry[]> {
    const results: ImportFileEntry[] = []
    if (entry.isFile) {
        try {
            const file = await new Promise<globalThis.File>((resolve, reject) =>
                (entry as FileSystemFileEntry).file(resolve, reject)
            )
            // Eagerly snapshot content — do NOT defer to a separate loop.
            // Safari recycles File backing storage during mass drag-and-drop,
            // causing arrayBuffer() to return wrong data when called later.
            const content = new Uint8Array(await file.arrayBuffer())
            results.push({ path: basePath + '/' + entry.name, content, mime: file.type || undefined })
        } catch (err) {
            // Skip unreadable files (e.g. system files, permission denied)
            console.warn(`[readEntryRecursive] Failed to read ${basePath}/${entry.name}:`, err)
        }
        // Yield to event loop every 20 entries to prevent UI freeze
        if (++counter.n % 20 === 0) await new Promise<void>(r => setTimeout(r, 0))
    } else if (entry.isDirectory) {
        const dirPath = basePath + '/' + entry.name
        const reader = (entry as FileSystemDirectoryEntry).createReader()
        let entries: FileSystemEntry[] = []
        // readEntries may return partial results — must loop
        let batch: FileSystemEntry[]
        do {
            batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject))
            entries = entries.concat(batch)
        } while (batch.length > 0)
        for (const child of entries) {
            const childResults = await readEntryRecursive(child, dirPath, counter)
            results.push(...childResults)
        }
    }
    return results
}

/** Check if a filename matches the search query (case-insensitive). */
function matchesSearch(name: string, query: string): boolean {
    if (!query) return true
    return name.toLowerCase().includes(query.toLowerCase())
}

/** Recursively collect all directories containing matches and count matching files. */
function collectMatchingPaths(
    node: TreeNode,
    query: string,
    matchingDirs: Set<string>,
    matchCount: { count: number }
): void {
    if (!query) return

    const queryLower = query.toLowerCase()
    const addAncestors = (path: string) => {
        for (const ancestor of buildAncestorPaths(path)) {
            matchingDirs.add(ancestor)
        }
    }

    if (isFileBackedVirtualFolder(node) && node.name.toLowerCase().includes(queryLower)) {
        matchCount.count++
        addAncestors(node.path)
        matchingDirs.add(node.path)
    }

    // Check files in this directory
    for (const file of node.files) {
        if (file.name.toLowerCase().includes(queryLower)) {
            matchCount.count++
            // Add all parent directories to matchingDirs
            addAncestors(file.path)
            if (isFileBackedVirtualFolder(node)) {
                matchingDirs.add(node.path)
            }
        }
    }

    // Check subdirectories
    for (const key of Object.keys(node.children)) {
        const child = node.children[key]
        collectMatchingPaths(child, query, matchingDirs, matchCount)
        // If child has matches, this directory is also a matching directory
        if (matchingDirs.has(child.path)) {
            const parts = child.path.split('/').filter(Boolean)
            let currentPath = ''
            for (let i = 0; i < parts.length - 1; i++) {
                currentPath += '/' + parts[i]
                matchingDirs.add(currentPath)
            }
            if (isFileBackedVirtualFolder(node)) {
                matchingDirs.add(node.path)
            }
        }
    }
}


// ── DirNode ────────────────────────────────────────────────────



interface DirNodeProps {
    name: string
    node: TreeNode
    depth: number
    selectedPath: string | null
    activeAgentPath: string | null
    activeTranscriptPath: string | null
    onSelect: (path: string | null) => void
    onOpenComponentJob?: (path: string) => void
    resolveJobSourcePath?: (jobRootPath: string) => string | null
    showInternals?: boolean
    onContextMenu: (x: number, y: number, type: ContextTargetType, path: string) => void
    dropTargetPath: string | null
    onDragOver: (path: string) => void
    onDrop: (e: DragEvent<HTMLElement>, path: string) => void
    onDragLeave: (e: React.DragEvent) => void
    canWrite: boolean
    inlineInput: InlineInputState | null
    onInlineConfirm: (val: string) => void
    onInlineCancel: () => void
    multiSelect: Set<string>
    selectionMode: boolean
    onToggleSelect: (path: string, range?: boolean) => void
    expandedDirs: Set<string>
    toggleDir: (path: string) => void
    workspace: Workspace
    showWriterDots: boolean
    searchQuery: string
    matchingDirs: Set<string>
    loadingDirs: ReadonlySet<string>
    loadedDirs: ReadonlySet<string>
    dirErrors: Readonly<Record<string, string>>
    onRetryDir: (path: string) => void
}

function DirNode({
    name, node, depth, selectedPath, activeAgentPath, activeTranscriptPath, onSelect, onOpenComponentJob, resolveJobSourcePath, showInternals, onContextMenu,
    dropTargetPath, onDragOver, onDrop, onDragLeave, canWrite,
    inlineInput, onInlineConfirm, onInlineCancel,
    multiSelect, selectionMode, onToggleSelect,
    expandedDirs, toggleDir, workspace, showWriterDots,
    searchQuery, matchingDirs, loadingDirs, loadedDirs, dirErrors, onRetryDir
}: DirNodeProps) {
    const longPressTriggered = useRef(false)
    const isExpanded = expandedDirs.has(node.path) || (name === '' && node.path === '/') // Expand root by default logic if needed

    // Attachment folders start collapsed unless in expandedDirs
    const expanded = node.isAttachmentFolder ? expandedDirs.has(node.path) : isExpanded

    // Virtual expansion for root (if name is empty) or if tracked
    const showChildren = name === '' ? true : expanded

    // Filter directories and files based on search query
    const visibleChildDirs = Object.keys(node.children)
        .filter(dirName => !(
            node.isComponentJobFolder
            && !node.parentFilePath
            && (dirName === '.secureblob' || dirName === '.job')
            && !showInternals
        ))

    const filteredDirs = searchQuery
        ? visibleChildDirs
            .filter(d => matchesSearch(d, searchQuery) || matchingDirs.has(node.children[d].path))
            .sort((a, b) => a.localeCompare(b, 'sv'))
        : visibleChildDirs.sort((a, b) => a.localeCompare(b, 'sv'))

    const filteredFiles = searchQuery
        ? [...node.files].filter(f => matchesSearch(f.name, searchQuery)).sort((a, b) => a.name.localeCompare(b.name, 'sv'))
        : [...node.files].sort((a, b) => a.name.localeCompare(b.name, 'sv'))

    const sortedDirs = filteredDirs
    const sortedFiles = filteredFiles
    const isDropTarget = dropTargetPath === node.path
    const isDirectoryLoading = loadingDirs.has(node.path)
    const dirError = dirErrors[node.path]
    const showNestedState = name !== '' && showChildren

    // Inline input checks
    const isCreatingFile = inlineInput?.mode === 'newFile' && inlineInput.parentPath === node.path
    const isCreatingFolder = (inlineInput?.mode === 'newFolder' || inlineInput?.mode === 'newJob') && inlineInput.parentPath === node.path
    const isCreatingJob = inlineInput?.mode === 'newJob' && inlineInput.parentPath === node.path
    const isRenamingDir = inlineInput?.mode === 'rename' && inlineInput.targetPath === node.path
    const isCurrentAgent = !!node.isAgentFolder && activeAgentPath === node.path

    const handleDirContext = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onContextMenu(
            e.clientX,
            e.clientY,
            isFileBackedVirtualFolder(node) ? 'file' : 'dir',
            node.parentFilePath ?? node.path,
        )
    }

    const handleDirDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onDragOver(node.path)
    }

    const handleNodeClick = (e: React.MouseEvent) => {
        if (longPressTriggered.current) {
            longPressTriggered.current = false
            e.preventDefault()
            e.stopPropagation()
            return
        }
        e.stopPropagation()
        if (selectionMode) {
            onToggleSelect(node.path)
            return
        }
        if (e.ctrlKey || e.metaKey) {
            onToggleSelect(node.path)
            return
        }
        if (e.shiftKey) {
            onToggleSelect(node.path, true)
            return
        }

        // Normal click: toggle expand
        toggleDir(node.path)

        // Special case: Attachment folder click also selects parent file
        if (node.isAttachmentFolder && node.parentFilePath) {
            onSelect(node.parentFilePath)
            return
        }

        if (node.isComponentJobFolder && node.parentFilePath) {
            onSelect(node.parentFilePath)
            return
        }

        if (node.isComponentJobFolder) {
            // For folder-first .job roots (no parentFilePath), resolve the actual
            // source file from the global file list via resolveJobSourcePath,
            // which checks real files instead of defaulting to the SDK's main.tsx.
            // This avoids opening a non-existent file when the real source uses a
            // different extension (e.g. main.ts).
            const resolvedSource = resolveJobSourcePath?.(node.path)
            if (resolvedSource) {
                onSelect(resolvedSource)
            } else {
                const componentJobPath = node.parentFilePath ?? node.path
                if (onOpenComponentJob) onOpenComponentJob(componentJobPath)
                else onSelect(componentJobPath)
            }
            return
        }

        if (node.isAgentFolder) {
            onSelect(node.path)
        }
    }

    // Mobile long-press
    const touchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const touchStartPos = useRef<{ x: number, y: number } | null>(null)

    const handleTouchStart = (e: React.TouchEvent, path: string, type: ContextTargetType) => {
        const startX = e.touches[0].clientX
        const startY = e.touches[0].clientY
        touchStartPos.current = { x: startX, y: startY }
        touchTimer.current = setTimeout(() => {
            longPressTriggered.current = true
            if (navigator.vibrate) navigator.vibrate(10)
            onToggleSelect(path)
            onContextMenu(startX, startY, type, path)
        }, 500)
    }

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!touchStartPos.current) return
        const dx = Math.abs(e.touches[0].clientX - touchStartPos.current.x)
        const dy = Math.abs(e.touches[0].clientY - touchStartPos.current.y)
        if (dx > 10 || dy > 10) {
            clearTimeout(touchTimer.current) // Cancel long-press on move
        }
    }

    const handleTouchEnd = () => {
        clearTimeout(touchTimer.current)
        touchStartPos.current = null
    }
    const handleTouchCancel = () => {
        clearTimeout(touchTimer.current)
        touchStartPos.current = null
    }

    const isSelected = multiSelect.has(node.path) || (selectionMode && multiSelect.has(node.path))

    return (
        <div className={`dir-node ${name ? 'dir-node--nested' : ''}`} style={{ '--node-depth': depth } as React.CSSProperties}>
            {name && (
                isRenamingDir ? (
                    <InlineTreeInput
                        depth={depth}
                        defaultValue={name}
                        icon={expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        onConfirm={onInlineConfirm}
                        onCancel={onInlineCancel}
                    />
                ) : (
                    <button
                        className={`tree-item tree-dir ${isDropTarget ? 'drop-target' : ''} ${node.isAttachmentFolder ? 'attachment-parent' : ''} ${selectedPath === node.path && !selectionMode ? 'selected' : ''} ${isSelected ? 'multi-selected' : ''}`}
                        style={{ paddingLeft: `${depth * INDENT_PX + 8}px` }}
                        onClick={handleNodeClick}
                        onContextMenu={handleDirContext}
                        onDragOver={handleDirDragOver}
                        onDrop={(e) => onDrop(e, node.path)}
                        onDragLeave={onDragLeave}
                        onTouchStart={(e) => handleTouchStart(e, node.parentFilePath ?? node.path, isFileBackedVirtualFolder(node) ? 'file' : 'dir')}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchCancel}
                        draggable={canWrite && !node.isAttachmentFolder && !node.isAgentFolder && !node.isComponentJobFolder}
                        onDragStart={(e) => {
                            if (node.isAttachmentFolder || node.isAgentFolder || node.isComponentJobFolder) { e.preventDefault(); return }
                            if (multiSelect.has(node.path) && multiSelect.size > 1) {
                                e.dataTransfer.setData('application/x-secureblob-multiselect', 'true')
                            } else {
                                e.dataTransfer.setData('application/x-secureblob-path', node.path)
                            }
                            e.dataTransfer.effectAllowed = 'move'
                        }}
                    >
                        {(selectionMode || multiSelect.size > 0) && (
                            <div className={`select-checkbox ${isSelected ? 'checked' : ''}`} onClick={(e) => {
                                e.stopPropagation()
                                onToggleSelect(node.path)
                            }} />
                        )}
                        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {node.isAgentFolder
                            ? <span className="icon-agent" title="AI Agent">🤖</span>
                            : node.isComponentJobFolder
                                ? <span className="icon-job" title="Component Job">🧩</span>
                            : node.isAttachmentFolder
                                ? <span className="icon-attachment" title="Attachments">📎</span>
                                : (expanded ? <FolderOpen size={16} /> : <Folder size={16} />)}
                        <span className="tree-label">{name}</span>
                        {node.isComponentJobFolder && node.parentFilePath && !node.path.endsWith('.job') && (
                            <span className="tree-inline-badge badge-legacy-job">Legacy Job</span>
                        )}
                        {isCurrentAgent && (
                            <span className="tree-inline-badge">Current</span>
                        )}
                    </button>
                )
            )}

            {showChildren && (
                <>
                    {/* Inline Input for New File/Folder (appearing first) */}
                    {isCreatingFolder && (
                        <InlineTreeInput
                            depth={name ? depth + 1 : depth}
                            defaultValue={isCreatingJob ? 'untitled.job' : ''}
                            icon={<Folder size={16} />}
                            onConfirm={onInlineConfirm}
                            onCancel={onInlineCancel}
                        />
                    )}
                    {isCreatingFile && (
                        <InlineTreeInput
                            depth={name ? depth + 1 : depth}
                            defaultValue="untitled.md"
                            icon={<File size={16} />}
                            onConfirm={onInlineConfirm}
                            onCancel={onInlineCancel}
                        />
                    )}

                    {sortedDirs.map(d => (
                        <DirNode
                            key={d}
                            name={d}
                            node={node.children[d]}
                            depth={name ? depth + 1 : depth}
                            selectedPath={selectedPath}
                            activeAgentPath={activeAgentPath}
                            activeTranscriptPath={activeTranscriptPath}
                            onSelect={onSelect}
                            onOpenComponentJob={onOpenComponentJob}
                            resolveJobSourcePath={resolveJobSourcePath}
                            showInternals={showInternals}
                            onContextMenu={onContextMenu}
                            dropTargetPath={dropTargetPath}
                            onDragOver={onDragOver}
                            onDrop={onDrop}
                            onDragLeave={onDragLeave}
                            canWrite={canWrite}
                            inlineInput={inlineInput}
                            onInlineConfirm={onInlineConfirm}
                            onInlineCancel={onInlineCancel}
                            multiSelect={multiSelect}
                            selectionMode={selectionMode}
                            onToggleSelect={onToggleSelect}
                            expandedDirs={expandedDirs}
                            toggleDir={toggleDir}
                            workspace={workspace}
                            showWriterDots={showWriterDots}
                            searchQuery={searchQuery}
                            matchingDirs={matchingDirs}
                            loadingDirs={loadingDirs}
                            loadedDirs={loadedDirs}
                            dirErrors={dirErrors}
                            onRetryDir={onRetryDir}
                        />
                    ))}
                    {showNestedState && isDirectoryLoading && (
                        <div
                            className="tree-directory-state"
                            style={{ paddingLeft: `${(depth + (name ? 1 : 0)) * INDENT_PX + 8 + CHEVRON_SPACE}px` }}
                        >
                            <span className="tree-directory-spinner" />
                            <span>Laddar…</span>
                        </div>
                    )}
                    {showNestedState && dirError && !isDirectoryLoading && (
                        <button
                            type="button"
                            className="tree-directory-state tree-directory-state--error"
                            style={{ paddingLeft: `${(depth + (name ? 1 : 0)) * INDENT_PX + 8 + CHEVRON_SPACE}px` }}
                            onClick={(e) => {
                                e.stopPropagation()
                                onRetryDir(node.path)
                            }}
                        >
                            <AlertTriangle size={13} />
                            <span>Kunde inte läsa mappen</span>
                        </button>
                    )}
                    {showNestedState
                        && !isDirectoryLoading
                        && !dirError
                        && loadedDirs.has(node.path)
                        && sortedDirs.length === 0
                        && sortedFiles.length === 0
                        && !isCreatingFolder
                        && !isCreatingFile
                        && (
                            <div
                                className="tree-directory-state"
                                style={{ paddingLeft: `${(depth + (name ? 1 : 0)) * INDENT_PX + 8 + CHEVRON_SPACE}px` }}
                            >
                                <span>Tom mapp</span>
                            </div>
                        )}
                    {sortedFiles.map(f => {
                        const isRenamingFile = inlineInput?.mode === 'rename' && inlineInput.targetPath === f.path
                        const isFileSelected = multiSelect.has(f.path)
                        const isActiveTranscript = f.isRunResult && activeTranscriptPath === f.path

                        if (isRenamingFile) {
                            return (
                                <InlineTreeInput
                                    key={f.path}
                                    depth={name ? depth + 1 : depth}
                                    defaultValue={f.name}
                                    icon={getFileIcon(f.name, f.isRunResult)}
                                    onConfirm={onInlineConfirm}
                                    onCancel={onInlineCancel}
                                />
                            )
                        }

                        const isJobConfigFile = node.isComponentJobFolder && !node.parentFilePath && f.name === 'job.json'
                        return (
                            <button
                                key={f.path}
                                className={`tree-item tree-file ${isJobConfigFile ? 'tree-file-secondary' : ''} ${selectedPath === f.path && !selectionMode ? 'selected' : ''} ${isActiveTranscript ? 'active-transcript' : ''} ${isFileSelected ? 'multi-selected' : ''}`}
                                style={{ paddingLeft: `${(depth + (name ? 1 : 0)) * INDENT_PX + 8 + CHEVRON_SPACE}px` }}
                                onClick={(e) => {
                                    if (longPressTriggered.current) {
                                        longPressTriggered.current = false
                                        e.preventDefault()
                                        e.stopPropagation()
                                        return
                                    }
                                    e.stopPropagation()
                                    if (selectionMode || e.ctrlKey || e.metaKey) {
                                        onToggleSelect(f.path)
                                        return
                                    }
                                    if (e.shiftKey) {
                                        onToggleSelect(f.path, true)
                                        return
                                    }
                                    onSelect(f.path)
                                }}
                                draggable={canWrite}
                                onDragStart={(e) => {
                                    // Multi-select drag support
                                    if (multiSelect.has(f.path) && multiSelect.size > 1) {
                                        // We can't put multiple paths in standard dataTransfer easily for file systems
                                        // But we can mark that we are moving the selection
                                        e.dataTransfer.setData('application/x-secureblob-multiselect', 'true')
                                        // Set a drag image with count (optional polish)
                                    } else {
                                        e.dataTransfer.setData('application/x-secureblob-path', f.path)
                                    }
                                    e.dataTransfer.effectAllowed = 'move'
                                }}
                                onContextMenu={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    // If right-clicking an unselected item, select it first (and clear others)
                                    // But handle that in parent or let context menu logic handle "if not selected"
                                    onContextMenu(e.clientX, e.clientY, 'file', f.path)
                                }}
                                onTouchStart={(e) => handleTouchStart(e, f.path, 'file')}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onTouchCancel={handleTouchCancel}
                            >
                                {(selectionMode || multiSelect.size > 0) && (
                                    <div className={`select-checkbox ${isFileSelected ? 'checked' : ''}`} onClick={(e) => {
                                        e.stopPropagation()
                                        onToggleSelect(f.path)
                                    }} />
                                )}
                                {getFileIcon(f.name, f.isRunResult)}
                                <span className="tree-label">{f.name}</span>
                                {isActiveTranscript && (
                                    <span className="tree-inline-badge">Current</span>
                                )}
                                <span className="tree-meta" title={f.mtime ? formatExactTime(fsIsTime(f.mtime)) : ''}>{formatMeta(f)}{f.mtime ? ` · ${new Date(fsIsTime(f.mtime)).toLocaleDateString()}` : ''}</span>
                                {showWriterDots && f.lastWriterKid && (
                                    <WriterDot kid={f.lastWriterKid} mtime={f.mtime} workspace={workspace} />
                                )}
                            </button>
                        )
                    })}
                    {node.isAgentFolder && (
                        <button
                            className="tree-item tree-file action-item"
                            style={{ paddingLeft: `${(depth + 1) * INDENT_PX + 8 + CHEVRON_SPACE}px` }}
                            onClick={() => onSelect(node.path)}
                        >
                            <Plus size={14} />
                            <span className="tree-label">Start ny chatt...</span>
                        </button>
                    )}
                </>
            )}
        </div>
    )
}

function fsIsTime(t: any): number { return typeof t === 'number' ? t : 0 } // Helper if mtime type is loose

function getExpandedAncestorPaths(path: string): string[] {
    const transcriptMatch = path.match(/^(.*?\.agent\.json)\.transcripts\/[^/]+\/transcript\.json$/)
    if (transcriptMatch) {
        const agentPath = transcriptMatch[1]
        const ancestors = buildAncestorPaths(agentPath)
        ancestors.push(agentPath)
        return ancestors
    }

    const componentJobPath = getComponentJobSourcePath(path)
    if (componentJobPath && componentJobPath !== path) {
        const ancestors = new Set(buildAncestorPaths(path))
        for (const ancestor of buildAncestorPaths(componentJobPath)) {
            ancestors.add(ancestor)
        }
        ancestors.add(componentJobPath)
        return [...ancestors]
    }

    return buildAncestorPaths(path)
}



// ── FileBrowser ────────────────────────────────────────────────

interface FileBrowserProps {
    selectedPath: string | null
    onSelect: (path: string | null) => void
    onOpenHistory?: (path: string) => void
    onOpenComponentJob?: (path: string) => void
    onPathsRenamed?: (renames: [string, string][]) => void
    onPathsDeleted?: (paths: string[]) => void
    canWrite?: boolean
}

export default function FileBrowser({ selectedPath, onSelect, onOpenHistory, onOpenComponentJob, onPathsRenamed, onPathsDeleted, canWrite = true }: FileBrowserProps) {
    const fileSystem = useFileSystem()
    const {
        files,
        writeFile,
        deleteFile,
        renameFile,
        readFile,
        mkdir,
        loadDir = async () => {},
        reloadDir = async () => {},
        loadingDirs = EMPTY_DIR_SET,
        loadedDirs = EMPTY_DIR_SET,
        dirErrors = EMPTY_DIR_ERRORS,
        isLoadingRoot = false,
    } = fileSystem
    const workspace = useWorkspace()
    const toast = useToast()

    const tree = useMemo(() => buildTree(files), [files])
    const activeAgentPath = useMemo(() => {
        if (!selectedPath) return null
        return selectedPath.endsWith('.agent.json') ? selectedPath : null
    }, [selectedPath])

    const activeTranscriptPath = useMemo(() => {
        if (!selectedPath) return null
        return /\.agent\.json\.transcripts\/.+\/transcript\.json$/.test(selectedPath)
            ? selectedPath
            : null
    }, [selectedPath])
    const showWriterDots = useMemo(() => {
        try { return workspace.getWriters().length > 1 } catch { return true }
    }, [workspace])
    const syncRenameEffects = useCallback((renames: [string, string][]) => {
        if (renames.length === 0) return
        onPathsRenamed?.(renames)
        if (!selectedPath) return
        const selectedRename = renames.find(([oldPath]) => oldPath === selectedPath)
        if (selectedRename) onSelect(selectedRename[1])
    }, [onPathsRenamed, onSelect, selectedPath])
    const syncDeleteEffects = useCallback((deletedPaths: string[]) => {
        if (deletedPaths.length === 0) return
        onPathsDeleted?.(deletedPaths)
        if (selectedPath && deletedPaths.includes(selectedPath)) {
            onSelect(null)
        }
    }, [onPathsDeleted, onSelect, selectedPath])
    const applyCascadeRename = useCallback((oldPath: string, newPath: string, fileSnapshot: FileStat[]) => {
        const renames = cascadeRename(oldPath, newPath, fileSnapshot, renameFile)
        syncRenameEffects(renames)
        return renames
    }, [renameFile, syncRenameEffects])

    // ── State ──────────────────────────────────────────────────────

    // Inline Input
    const [inlineInput, setInlineInput] = useState<InlineInputState | null>(null)

    // Show .secureblob/ internals toggle
    const [showInternals, setShowInternals] = useState(false)
    const hasFolderFirstJobs = useMemo(() => files.some(f => f.kind === 'directory' && f.path.endsWith('.job')), [files])

    // Multi-select
    const [multiSelect, setMultiSelect] = useState<Set<string>>(new Set())
    const [selectionMode, setSelectionMode] = useState(false)
    const lastClickedPath = useRef<string | null>(null)

    // Expansion (Lifted state)
    const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set())
    const toggleDir = useCallback((path: string) => {
        const normalized = normalizeBrowserDirPath(path)
        setExpandedDirs(prev => {
            const next = new Set(prev)
            if (next.has(normalized)) {
                next.delete(normalized)
            } else {
                next.add(normalized)
                void loadDir(normalized)
            }
            return next
        })
    }, [loadDir])
    const resolveJobSourcePath = useCallback((jobRootPath: string): string | null => {
        return resolveComponentJobSourcePath(jobRootPath, files)
    }, [files])
    // Initialize root expansion
    useEffect(() => {
        if (files.length > 0 && expandedDirs.size === 0) {
            setExpandedDirs(new Set(['/']))
        }
    }, [files.length]) // Run once when files load

    useEffect(() => {
        if (!selectedPath) return
        const ancestorPaths = getExpandedAncestorPaths(selectedPath)
        setExpandedDirs(prev => {
            const next = new Set(prev)
            let changed = false
            for (const ancestor of ancestorPaths) {
                const normalized = normalizeBrowserDirPath(ancestor)
                if (!next.has(normalized)) {
                    next.add(normalized)
                    changed = true
                }
            }
            return changed ? next : prev
        })
        for (const ancestor of ancestorPaths) {
            void loadDir(ancestor)
        }
    }, [selectedPath, loadDir])

    // Drag & Drop
    const [dropTargetPath, setDropTargetPath] = useState<string | null>(null)
    const [isDraggingOver, setIsDraggingOver] = useState(false)
    const dragCounter = useRef(0)
    const autoExpandTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    // Context Menu
    const [ctxMenu, setCtxMenu] = useState<CtxMenuState | null>(null)
    const [contextMenuSections, setContextMenuSections] = useState<MenuSection[] | undefined>(undefined)

    // Upload
    const [uploadProgress, setUploadProgress] = useState<{
        current: number
        total: number
        currentName: string
        replaced: number
        cancelling?: 'discard' | 'keep' | null
        phase?: 'encrypting' | 'saving'
    } | null>(null)
    const abortControllerRef = useRef<AbortController | null>(null)

    // Search
    const [searchQuery, setSearchQuery] = useState('')
    const searchInputRef = useRef<HTMLInputElement | null>(null)

    // Confirmation modal
    const [pendingConfirm, setPendingConfirm] = useState<ConfirmState | null>(null)

    // Compute matching directories for search
    const matchingDirs = useMemo(() => {
        if (!searchQuery.trim()) return new Set<string>()
        const dirs = new Set<string>()
        const count = { count: 0 }
        collectMatchingPaths(tree, searchQuery.trim(), dirs, count)
        return dirs
    }, [tree, searchQuery])

    // Compute match count for search badge
    const searchMatchCount = useMemo(() => {
        if (!searchQuery.trim()) return 0
        let count = 0
        function countMatches(node: TreeNode, query: string): void {
            const queryLower = query.toLowerCase()
            if (isFileBackedVirtualFolder(node) && node.name.toLowerCase().includes(queryLower)) {
                count++
            }
            for (const file of node.files) {
                if (file.name.toLowerCase().includes(queryLower)) count++
            }
            for (const key of Object.keys(node.children)) {
                countMatches(node.children[key], query)
            }
        }
        countMatches(tree, searchQuery.trim())
        return count
    }, [tree, searchQuery])

    // Auto-expand directories containing matches
    useEffect(() => {
        if (searchQuery.trim() && matchingDirs.size > 0) {
            setExpandedDirs(prev => {
                const next = new Set(prev)
                matchingDirs.forEach(dir => next.add(dir))
                return next
            })
        }
    }, [searchQuery, matchingDirs])

    // ── Paste from clipboard ────────────────────────────────────
    const handlePasteFromClipboard = useCallback(async (targetDir = '/') => {
        try {
            const ts = new Date().toTimeString().slice(0, 8).replace(/:/g, '')
            const prefix = targetDir === '/' ? '' : targetDir

            if (navigator.clipboard.read) {
                const items = await navigator.clipboard.read()

                // ── Phase 1: Collect all content ────────────
                const imageBlobs: { blob: Blob; ext: string }[] = []
                let textContent = ''

                for (const item of items) {
                    const imageType = item.types.find((t: string) => t.startsWith('image/'))
                    if (imageType) {
                        const blob = await item.getType(imageType)
                        const ext = imageExtensionFromMimeType(imageType)
                        imageBlobs.push({ blob, ext })
                    }
                    // Prefer text/html for rich content (Excel tables, Word formatting)
                    if (item.types.includes('text/html') && !textContent) {
                        try {
                            const blob = await item.getType('text/html')
                            const html = await blob.text()
                            if (hasRichHtmlContent(html)) {
                                const md = htmlToMarkdown(html)
                                if (md.trim()) textContent = md
                            }
                        } catch { /* fall through to text/plain */ }
                    }
                    if (item.types.includes('text/plain') && !textContent) {
                        const blob = await item.getType('text/plain')
                        const t = await blob.text()
                        if (t.trim()) textContent = t
                    }
                }

                // ── Phase 2: Save based on content mix ──────
                if (textContent && imageBlobs.length > 0) {
                    // Mixed content: create .md with text + images in companion folder
                    const mdPath = `${prefix}/paste-${ts}.md`
                    const companionDir = `${prefix}/paste-${ts}.md.attachments`
                    let mdContent = textContent + '\n\n'
                    for (let i = 0; i < imageBlobs.length; i++) {
                        const { blob, ext } = imageBlobs[i]
                        const imgName = `image-${i + 1}.${ext}`
                        const imgPath = `${companionDir}/${imgName}`
                        const buffer = await blob.arrayBuffer()
                        workspace.fs.writeBytes(imgPath, new Uint8Array(buffer))
                        mdContent += `![image](paste-${ts}.md.attachments/${imgName})\n`
                    }
                    writeFile(mdPath, mdContent)
                    onSelect(mdPath)
                    setExpandedDirs(prev => new Set([...prev, companionDir]))
                    toast(`Skapade ${mdPath.split('/').pop()} med ${imageBlobs.length} bild(er)`, {
                        type: 'success', duration: 6000,
                        action: {
                            label: 'Byt namn', onClick: () => {
                                const parentDir = mdPath.substring(0, mdPath.lastIndexOf('/')) || '/'
                                if (parentDir !== '/') setExpandedDirs(prev => new Set([...prev, parentDir]))
                                setInlineInput({ mode: 'rename', parentPath: '', targetPath: mdPath, currentName: mdPath.split('/').pop() || '' })
                            }
                        }
                    })
                    return
                }

                if (imageBlobs.length > 0) {
                    // Images only → save to /images/
                    for (let i = 0; i < imageBlobs.length; i++) {
                        const { blob, ext } = imageBlobs[i]
                        const suffix = imageBlobs.length > 1 ? `-${i + 1}` : ''
                        const path = `/images/paste-${ts}${suffix}.${ext}`
                        const buffer = await blob.arrayBuffer()
                        workspace.fs.writeBytes(path, new Uint8Array(buffer))
                        if (i === imageBlobs.length - 1) {
                            onSelect(path)
                            toast(`Skapade ${imageBlobs.length > 1 ? `${imageBlobs.length} bilder` : path.split('/').pop()}`, {
                                type: 'success', duration: 6000,
                                action: {
                                    label: 'Byt namn', onClick: () => {
                                        setExpandedDirs(prev => new Set([...prev, '/images']))
                                        setInlineInput({ mode: 'rename', parentPath: '', targetPath: path, currentName: path.split('/').pop() || '' })
                                    }
                                }
                            })
                        }
                    }
                    return
                }

                if (textContent) {
                    // Text only → smart extension detection
                    const ext = detectFileExtension(textContent)
                    const path = `${prefix}/paste-${ts}${ext}`
                    writeFile(path, textContent)
                    onSelect(path)
                    toast(`Skapade ${path.split('/').pop()}`, {
                        type: 'success', duration: 6000,
                        action: {
                            label: 'Byt namn', onClick: () => {
                                const parentDir = path.substring(0, path.lastIndexOf('/')) || '/'
                                if (parentDir !== '/') setExpandedDirs(prev => new Set([...prev, parentDir]))
                                setInlineInput({ mode: 'rename', parentPath: '', targetPath: path, currentName: path.split('/').pop() || '' })
                            }
                        }
                    })
                    return
                }
            } else {
                const text = await navigator.clipboard.readText()
                if (text?.trim()) {
                    const ext = detectFileExtension(text)
                    const path = `${prefix}/paste-${ts}${ext}`
                    writeFile(path, text)
                    onSelect(path)
                    toast(`Skapade ${path.split('/').pop()}`, {
                        type: 'success', duration: 6000,
                        action: {
                            label: 'Byt namn', onClick: () => {
                                const parentDir = path.substring(0, path.lastIndexOf('/')) || '/'
                                if (parentDir !== '/') setExpandedDirs(prev => new Set([...prev, parentDir]))
                                setInlineInput({ mode: 'rename', parentPath: '', targetPath: path, currentName: path.split('/').pop() || '' })
                            }
                        }
                    })
                    return
                }
            }
            toast('Inget att klistra in', { type: 'info' })
        } catch {
            toast('Kunde inte läsa urklipp', { type: 'error' })
        }
    }, [writeFile, workspace, onSelect, toast, setExpandedDirs, setInlineInput])

    // ── Handlers ───────────────────────────────────────────────────

    const onInlineConfirm = (val: string) => {
        if (!inlineInput) return

        const scaffoldJob = async (jobPath: string) => {
            try {
                await scaffoldNewComponentJobRoot({ jobRootPath: jobPath, readFile, mkdir, writeFile })
                setExpandedDirs(prev => new Set([...prev, jobPath]))
                if (onOpenComponentJob) onOpenComponentJob(jobPath)
                else onSelect(jobPath)
                toast(`Created job "${jobPath.split('/').pop()}"`, { type: 'success' })
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown error'
                toast(`Failed to create component job: ${message}`, { type: 'error' })
            }
        }

        // Name validation
        if (!val || val.trim() === '') {
            setInlineInput(null)
            return
        }
        if (val.includes('/')) {
            toast('Name cannot contain "/"', { type: 'error' })
            return
        }

        // 1. New File
        if (inlineInput.mode === 'newFile') {
            const newPath = `${inlineInput.parentPath === '/' ? '' : inlineInput.parentPath}/${val}`
            if (files.some(f => f.path === newPath)) {
                toast(`File "${val}" already exists`, { type: 'error' })
                return
            }
            // sqlite3mt databases are logical page-manifest stores. A new .db path is
            // only a placeholder entry in the workspace tree; first open bootstraps
            // from the synthetic empty root and materializes touched pages on demand.
            // Writing a full SQLite header blob here would violate §96/§97's
            // O(berörda pages) transport model.
            writeFile(newPath)
            onSelect(newPath)
        }
        // 2. New Folder
        else if (inlineInput.mode === 'newFolder' || inlineInput.mode === 'newJob') {
            const folderName = inlineInput.mode === 'newJob' && !val.endsWith('.job')
                ? `${val}.job`
                : val
            const newPath = `${inlineInput.parentPath === '/' ? '' : inlineInput.parentPath}/${folderName}`
            if (files.some(f => f.path === newPath || f.path === newPath + '/')) {
                toast(`Folder "${folderName}" already exists`, { type: 'error' })
                return
            }
            if (folderName.endsWith('.job')) {
                void scaffoldJob(newPath)
            } else {
                mkdir(newPath)
                setExpandedDirs(prev => new Set([...prev, newPath]))
            }
        }
        // 3. Rename
        else if (inlineInput.mode === 'rename' && inlineInput.targetPath) {
            const oldPath = inlineInput.targetPath
            // Replace the last path segment so root-level renames keep their leading slash.
            const pathParts = oldPath.split('/')
            pathParts.pop()
            const builtNewPath = [...pathParts, val].join('/')

            if (builtNewPath !== oldPath) {
                if (files.some(f => f.path === builtNewPath || f.path === builtNewPath + '/')) {
                    toast(`"${val}" already exists`, { type: 'error' })
                    return
                }
                applyCascadeRename(oldPath, builtNewPath, files)

                // Add undo toast — use workspace.fs.list() (current state) instead
                // of the stale React `files` snapshot, so companions and directory
                // children are correctly restored on undo.
                toast(`Renamed to "${val}"`, {
                    type: 'info',
                    duration: 6000,
                    action: {
                        label: 'Undo',
                        onClick: () => applyCascadeRename(builtNewPath, oldPath, workspace.fs.list())
                    }
                })
            }
        }
        setInlineInput(null)
    }

    const onInlineCancel = () => setInlineInput(null)

    const onToggleSelect = useCallback((path: string, range = false) => {
        setMultiSelect(prev => {
            const next = new Set(prev)
            if (range && lastClickedPath.current) {
                // To implement range selection properly, we need the linear list of visible items.
                // For this step, we'll simplify and just add/toggle the item, acknowledging limitation.
                // Improvement: passed 'tree' and 'expandedDirs' to a utility to get range.
                // For now:
                next.add(path)
            } else {
                if (next.has(path)) next.delete(path)
                else next.add(path)
            }
            lastClickedPath.current = path
            return next
        })

        // If on mobile/touch, ensure selection mode is on
        if (!selectionMode && window.matchMedia('(pointer: coarse)').matches) {
            setSelectionMode(true)
        }
    }, [selectionMode])


    // ── Context Menu Logic ─────────────────────────────────────────

    const hideCtxMenu = useCallback(() => setCtxMenu(null), [])

    const buildMenuSections = useCallback((targetType: ContextTargetType, targetPath: string, selectedPaths: Set<string> = multiSelect): MenuSection[] => {
        const sections: MenuSection[] = []

        // Bulk Actions
        if (selectedPaths.size >= 2 && selectedPaths.has(targetPath)) {
            const count = selectedPaths.size
            sections.push({
                items: [{ icon: <Copy size={14} />, label: 'Copy paths', action: 'bulkCopyPaths' }]
            })

            if (canWrite) {
                sections.push({
                    items: [{ icon: <Trash2 size={14} />, label: `Delete ${count} items`, action: 'bulkDelete', danger: true }]
                })
            }
            return sections
        }

        // Single Actions
        if (targetType === 'file') {
            const componentJobRootPath = getComponentJobRootPath(targetPath)
            const componentJobSourcePath = resolveComponentJobSourcePath(targetPath, files) ?? getComponentJobSourcePath(targetPath)
            const isDirectoryTarget = files.some(file => file.path === targetPath && file.kind === 'directory')
            const isComponentJobRoot = componentJobRootPath === targetPath
            const isComponentJobSource = componentJobSourcePath === targetPath
            const sourceFileName = componentJobSourcePath?.split('/').pop() ?? 'main.tsx'
            const starterScaffolds = isComponentJobRoot
                ? listComponentJobStarterScaffoldsForJobRoot(targetPath)
                : (isComponentJobSource && componentJobSourcePath
                    ? (componentJobRootPath
                        ? listComponentJobStarterScaffoldsForJobRoot(componentJobRootPath)
                        : listComponentJobStarterScaffoldsForSourceFile(componentJobSourcePath))
                    : [])
            const quickItems: MenuEntry[] = []
            if (canWrite) {
                quickItems.push({ icon: <Edit3 size={14} />, label: 'Rename', action: 'rename' })
                if (isDirectoryTarget) {
                    quickItems.push({ icon: <FilePlus size={14} />, label: 'New file', action: 'newFile' })
                    quickItems.push({ icon: <FolderPlus size={14} />, label: 'New folder', action: 'newFolder' })
                } else {
                    quickItems.push({ icon: <Files size={14} />, label: 'Duplicate', action: 'duplicate' })
                }
            }
            if (!isDirectoryTarget) {
                quickItems.push({ icon: <History size={14} />, label: 'File history', action: 'fileHistory' })
            }
            quickItems.push({ icon: <Copy size={14} />, label: 'Copy path', action: 'copyPath' })
            sections.push({ items: quickItems })

            if (isComponentJobRoot) {
                sections.push({
                    title: 'Component Job',
                    items: [
                        { icon: <span style={{ fontSize: '14px' }}>🧩</span>, label: 'Open overview', action: 'openComponentJobOverview' },
                        { icon: <File size={14} />, label: `Open ${sourceFileName}`, action: 'openComponentJobSource' },
                        { icon: <File size={14} />, label: 'Open config', action: 'openComponentJobConfig' },
                        ...(canWrite
                            ? [
                                { icon: <Files size={14} />, label: 'Duplicate job', action: 'duplicateJob' },
                                ...starterScaffolds.map(scaffold => ({
                                    icon: <span style={{ fontSize: '14px' }}>🛠️</span>,
                                    label: `Create ${scaffold.label.toLowerCase()} starter`,
                                    action: `${COMPONENT_JOB_STARTER_ACTION_PREFIX}${scaffold.template}`,
                                })),
                            ]
                            : []),
                    ],
                })
            } else if (isComponentJobSource) {
                const isLegacySourceFirst = componentJobRootPath === null
                sections.push({
                    title: 'Component Job',
                    items: [
                        { icon: <span style={{ fontSize: '14px' }}>🧩</span>, label: isLegacySourceFirst ? 'Open legacy job controls' : 'Open job controls', action: 'openComponentJobOverview' },
                        ...(canWrite
                            ? starterScaffolds.map(scaffold => ({
                                icon: <span style={{ fontSize: '14px' }}>🛠️</span>,
                                label: `Create ${scaffold.label.toLowerCase()} starter`,
                                action: `${COMPONENT_JOB_STARTER_ACTION_PREFIX}${scaffold.template}`,
                            }))
                            : []),
                        ...(canWrite && isLegacySourceFirst
                            ? [{ icon: <span style={{ fontSize: '14px' }}>📁</span>, label: 'Convert to job folder', action: 'convertToJobFolder' }]
                            : []),
                    ],
                })
            }

            if (canWrite) {
                const moveItems: MenuEntry[] = []
                moveItems.push({ icon: <FolderPlus size={14} />, label: 'Another folder...', action: 'moveTo' })
                sections.push({ title: 'Move to', icon: <ArrowRight size={12} />, items: moveItems, defaultCollapsed: true })
                sections.push({ items: [{ icon: <Trash2 size={14} />, label: 'Delete', action: 'delete', danger: true }] })
            }
        } else if (targetType === 'dir') {
            const componentJobRootPath = getComponentJobRootPath(targetPath)
            const isComponentJobRoot = componentJobRootPath === targetPath

            if (isComponentJobRoot) {
                const starterScaffolds = listComponentJobStarterScaffoldsForJobRoot(targetPath)
                const resolvedSourcePath = resolveComponentJobSourcePath(targetPath, files) ?? getComponentJobSourcePath(targetPath)
                const dirSourceFileName = resolvedSourcePath?.split('/').pop() ?? 'main.tsx'
                sections.push({
                    title: 'Component Job',
                    items: [
                        { icon: <span style={{ fontSize: '14px' }}>🧩</span>, label: 'Open overview', action: 'openComponentJobOverview' },
                        { icon: <File size={14} />, label: `Open ${dirSourceFileName}`, action: 'openComponentJobSource' },
                        { icon: <File size={14} />, label: 'Open config', action: 'openComponentJobConfig' },
                        ...(canWrite
                            ? [
                                { icon: <Files size={14} />, label: 'Duplicate job', action: 'duplicateJob' },
                                ...starterScaffolds.map(scaffold => ({
                                    icon: <span style={{ fontSize: '14px' }}>🛠️</span>,
                                    label: `Create ${scaffold.label.toLowerCase()} starter`,
                                    action: `${COMPONENT_JOB_STARTER_ACTION_PREFIX}${scaffold.template}`,
                                })),
                            ]
                            : []),
                    ],
                })
            }

            if (canWrite) {
                sections.push({
                    items: [
                        { icon: <FilePlus size={14} />, label: 'New file', action: 'newFile' },
                        { icon: <FolderPlus size={14} />, label: 'New folder', action: 'newFolder' },
                        { icon: <span style={{ fontSize: '14px' }}>🧩</span>, label: 'New job', action: 'newJob' },
                        { icon: <ClipboardPaste size={14} />, label: 'Klistra in', action: 'paste' },
                    ]
                })
                sections.push({ items: [{ icon: <Edit3 size={14} />, label: 'Rename', action: 'rename' }] })
                sections.push({ items: [{ icon: <Trash2 size={14} />, label: 'Delete', action: 'delete', danger: true }] })
            }
        } else { // Root
            if (canWrite) {
                sections.push({
                    items: [
                        { icon: <FilePlus size={14} />, label: 'New file', action: 'newFile' },
                        { icon: <FolderPlus size={14} />, label: 'New folder', action: 'newFolder' },
                        { icon: <span style={{ fontSize: '14px' }}>🧩</span>, label: 'New job', action: 'newJob' },
                        { icon: <Upload size={14} />, label: 'Upload', action: 'upload' },
                        { icon: <ClipboardPaste size={14} />, label: 'Klistra in', action: 'paste' },
                    ]
                })
            }
        }
        return sections
    }, [multiSelect, canWrite, files])

    const showCtxMenu = useCallback((x: number, y: number, targetType: ContextTargetType, targetPath: string) => {
        let effectiveMultiSelect = multiSelect
        if (targetType === 'file' || targetType === 'dir') {
            if (!multiSelect.has(targetPath)) {
                setMultiSelect(new Set())
                effectiveMultiSelect = new Set()
            }
        }
        setContextMenuSections(buildMenuSections(targetType, targetPath, effectiveMultiSelect))
        setCtxMenu({ x, y, targetType, targetPath })
    }, [buildMenuSections, multiSelect])

    const handleCtxAction = async (action: string) => {
        if (!ctxMenu) return
        const { targetPath } = ctxMenu
        // Let ContextMenu run its own close animation; onClose will clear ctxMenu.

        // Bulk Actions
        if (action === 'bulkDelete') {
            const count = multiSelect.size
            setPendingConfirm({
                title: `Delete ${count} items?`,
                message: `This will permanently delete ${count} selected items and their contents.`,
                confirmLabel: 'Delete',
                onConfirm: () => {
                    setPendingConfirm(null)
                    const allTargets = new Set<string>()
                    for (const p of multiSelect) {
                        collectDeleteTargets(p, files).forEach(t => allTargets.add(t))
                    }
                    const deletedPaths = [...allTargets]
                    for (const p of deletedPaths) deleteFile(p)
                    syncDeleteEffects(deletedPaths)
                    setMultiSelect(new Set())
                    setSelectionMode(false)
                    toast(`Deleted ${count} items`, { type: 'info' })
                },
            })
            return
        }

        // Single Actions
        if (action === 'rename') {
            if (targetPath.includes('.attachments/') || targetPath.includes('.transcripts/') || isComponentJobInternalPath(targetPath)) {
                toast('Cannot rename internal files', { type: 'error' })
                return
            }
            const name = targetPath.split('/').pop() || ''
            setInlineInput({
                mode: 'rename',
                parentPath: '',
                targetPath,
                currentName: name
            })
        }
        else if (action === 'newFile') {
            setInlineInput({ mode: 'newFile', parentPath: targetPath })
            if (targetPath !== '/') setExpandedDirs(prev => new Set([...prev, targetPath]))
        }
        else if (action === 'newFolder') {
            setInlineInput({ mode: 'newFolder', parentPath: targetPath })
            if (targetPath !== '/') setExpandedDirs(prev => new Set([...prev, targetPath]))
        }
        else if (action === 'newJob') {
            setInlineInput({ mode: 'newJob', parentPath: targetPath })
            if (targetPath !== '/') setExpandedDirs(prev => new Set([...prev, targetPath]))
        }
        else if (action === 'delete') {
            const name = targetPath.split('/').pop()
            setPendingConfirm({
                title: `Delete "${name}"?`,
                message: 'This item and its contents will be permanently deleted.',
                confirmLabel: 'Delete',
                onConfirm: () => {
                    setPendingConfirm(null)
                    const targets = collectDeleteTargets(targetPath, files)
                    for (const p of targets) deleteFile(p)
                    syncDeleteEffects(targets)
                    toast(`Deleted "${name}"`, { type: 'info' })
                },
            })
        }
        else if (action === 'duplicate') {
            const content = await readFile(targetPath)
            if (content === null) return
            const parts = targetPath.split('/')
            const name = parts.pop()!
            const parent = parts.join('/')

            const [base, ext] = name.includes('.') ? [name.substring(0, name.lastIndexOf('.')), name.substring(name.lastIndexOf('.'))] : [name, '']
            let newName = `${base} copy${ext}`
            let i = 2
            while (files.some(f => f.path === `${parent}/${newName}` || f.path === `${parent}/${newName}/`)) {
                newName = `${base} copy ${i}${ext}`
                i++
            }
            writeFile(`${parent}/${newName}`, content)
        }
        else if (action === 'duplicateJob') {
            const jobRoot = getComponentJobRootPath(targetPath)
            if (!jobRoot) {
                toast('Not a component job folder', { type: 'error' })
                return
            }
            const jobRootName = jobRoot.split('/').pop()!
            const parentParts = jobRoot.split('/')
            parentParts.pop()
            const parent = parentParts.join('/') || '/'

            const baseName = jobRootName.endsWith('.job') ? jobRootName.slice(0, -4) : jobRootName
            let newName = `${baseName} copy.job`
            let i = 2
            const prefix = parent === '/' ? '' : parent
            while (files.some(f => f.path === `${prefix}/${newName}` || f.path === `${prefix}/${newName}/`)) {
                newName = `${baseName} copy ${i}.job`
                i++
            }
            const newPath = `${prefix}/${newName}`

            // Collect user-owned files; operational state and artifacts stay behind.
            const userFiles = files.filter(f =>
                f.path.startsWith(jobRoot + '/') &&
                !f.path.startsWith(jobRoot + '/.secureblob/') &&
                !f.path.startsWith(jobRoot + '/.job/') &&
                f.kind !== 'directory'
            )

            // Collect unique subdirectories
            const subDirs = new Set<string>()
            for (const f of userFiles) {
                const relativePath = f.path.slice(jobRoot.length + 1)
                const parts = relativePath.split('/')
                if (parts.length > 1) {
                    let current = newPath
                    for (let j = 0; j < parts.length - 1; j++) {
                        current += '/' + parts[j]
                        subDirs.add(current)
                    }
                }
            }

            mkdir(newPath)
            for (const dir of subDirs) {
                mkdir(dir)
            }
            mkdir(`${newPath}/.secureblob`)
            mkdir(`${newPath}/.secureblob/runs`)
            mkdir(`${newPath}/.secureblob/artifacts`)
            mkdir(`${newPath}/.secureblob/builds`)

            for (const f of userFiles) {
                const relativePath = f.path.slice(jobRoot.length + 1)
                const content = await readFile(f.path)
                if (content !== null) {
                    writeFile(`${newPath}/${relativePath}`, content)
                }
            }

            setExpandedDirs(prev => new Set([...prev, newPath]))
            toast(`Duplicated job as "${newName}"`, { type: 'success' })
        }
        else if (action === 'copyPath') {
            navigator.clipboard.writeText(targetPath)
            toast('Path copied', { type: 'success' })
        }
        else if (action === 'fileHistory') {
            onOpenHistory?.(targetPath)
        }
        else if (action === 'openComponentJobOverview') {
            const overviewPath = getComponentJobRootPath(targetPath) ?? targetPath
            if (onOpenComponentJob) onOpenComponentJob(overviewPath)
            else onSelect(overviewPath)
        }
        else if (action === 'openComponentJobSource') {
            const sourcePath = resolveComponentJobSourcePath(targetPath, files) ?? getComponentJobSourcePath(targetPath)
            if (!sourcePath) {
                toast('Component job source could not be resolved', { type: 'error' })
                return
            }
            onSelect(sourcePath)
        }
        else if (action === 'openComponentJobConfig') {
            const sourcePath = resolveComponentJobSourcePath(targetPath, files) ?? getComponentJobSourcePath(targetPath)
            if (!sourcePath) {
                toast('Component job config could not be resolved', { type: 'error' })
                return
            }
            const { settings } = getComponentJobCompanionPaths(sourcePath)
            if (onOpenComponentJob && getComponentJobRootPath(settings)) onOpenComponentJob(settings)
            else onSelect(settings)
        }
        else if (action.startsWith(COMPONENT_JOB_STARTER_ACTION_PREFIX)) {
            const starterTemplate = action.slice(COMPONENT_JOB_STARTER_ACTION_PREFIX.length) as ComponentJobStarterTemplate
            const sourcePath = resolveComponentJobSourcePath(targetPath, files) ?? getComponentJobSourcePath(targetPath)
            if (!sourcePath) {
                toast('Component job starter is unavailable here', { type: 'error' })
                return
            }
            const jobRootPath = getComponentJobRootPath(targetPath) ?? getComponentJobRootPath(sourcePath)
            if (targetPath !== sourcePath && jobRootPath === null) {
                toast('Component job starter is only available on source files', { type: 'error' })
                return
            }

            try {
                if (jobRootPath) {
                    const scaffold = createComponentJobScaffold(jobRootPath, { template: starterTemplate })
                    const shouldConfirm = (await Promise.all(scaffold.files.map(file =>
                        readFile(`${jobRootPath}/${file.path}`),
                    ))).some(content => typeof content === 'string' && content.trim().length > 0)
                    const applyScaffold = async () => {
                        try {
                            setPendingConfirm(null)
                        } catch {
                            // Confirmation dialog already dismissed — abort without crashing.
                            return
                        }
                        try {
                            const applied = await applyStarterScaffoldToJobRoot({
                                jobRootPath,
                                template: scaffold.template,
                                readFile,
                                mkdir,
                                writeFile,
                            })
                            if (onOpenComponentJob) onOpenComponentJob(jobRootPath)
                            else onSelect(jobRootPath)
                            toast(`${applied.label} starter ready in ${jobRootPath.split('/').pop()}`, { type: 'success' })
                        } catch (error) {
                            const message = error instanceof Error ? error.message : 'Unknown error'
                            toast(`Failed to create component job starter: ${message}`, { type: 'error' })
                        }
                    }

                    if (shouldConfirm) {
                        setPendingConfirm({
                            title: `Replace starter in "${jobRootPath.split('/').pop()}"?`,
                            message: `This will replace scaffold-owned files with the ${scaffold.label} starter. SecureBlob will not touch job.json or .secureblob internals.`,
                            confirmLabel: 'Replace',
                            onConfirm: () => { void applyScaffold() },
                        })
                        return
                    }

                    await applyScaffold()
                    return
                }

                const scaffold = createSourceCompatibleComponentJobScaffold(sourcePath, starterTemplate)
                if (!scaffold) {
                    toast('This starter requires a folder-first .job root. Convert the file to a job folder first.', { type: 'error' })
                    return
                }

                const existingSource = await readFile(sourcePath)
                const applySourceStarter = () => {
                    try {
                        setPendingConfirm(null)
                    } catch {
                        // Confirmation dialog already dismissed — abort without crashing.
                        return
                    }
                    try {
                        writeFile(sourcePath, scaffold.files[0].content)
                        if (onOpenComponentJob) onOpenComponentJob(sourcePath)
                        else onSelect(sourcePath)
                        toast(`${scaffold.label} starter ready in ${sourcePath.split('/').pop()}`, { type: 'success' })
                    } catch (error) {
                        const message = error instanceof Error ? error.message : 'Unknown error'
                        toast(`Failed to create component job starter: ${message}`, { type: 'error' })
                    }
                }

                if (!isBlankComponentJobSource(existingSource)) {
                    setPendingConfirm({
                        title: `Replace "${sourcePath.split('/').pop()}"?`,
                        message: `This will replace the existing source with the ${scaffold.label} starter.`,
                        confirmLabel: 'Replace',
                        onConfirm: applySourceStarter,
                    })
                    return
                }

                applySourceStarter()
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown error'
                toast(`Failed to create component job starter: ${message}`, { type: 'error' })
            }
        }
        else if (action === 'convertToJobFolder') {
            const sourcePath = resolveComponentJobSourcePath(targetPath, files) ?? getComponentJobSourcePath(targetPath)
            if (!sourcePath || getComponentJobRootPath(targetPath) !== null) {
                toast('Only source-first component jobs can be converted', { type: 'error' })
                return
            }

            let plan
            try {
                plan = deriveComponentJobConversionPlan(sourcePath)
            } catch {
                toast('Cannot convert this file to a job folder', { type: 'error' })
                return
            }

            if (files.some(f => f.path === plan.jobRootPath || f.path.startsWith(plan.jobRootPath + '/'))) {
                toast(`"${plan.jobRootPath}" already exists — rename or delete it first`, { type: 'error' })
                return
            }

            const sourceName = sourcePath.split('/').pop()
            setPendingConfirm({
                title: `Convert "${sourceName}" to a job folder?`,
                message: `This will create ${plan.jobRootPath}/ with main code, config, and internal history. The original file and its companions will be removed.`,
                confirmLabel: 'Convert',
                onConfirm: async () => {
                    setPendingConfirm(null)
                    try {
                const { settings: oldSettings, runsDir: oldRunsDir, buildsDir: oldBuildsDir } = getComponentJobCompanionPaths(sourcePath)

                // Create job folder structure
                mkdir(plan.jobRootPath)
                mkdir(`${plan.jobRootPath}/.secureblob`)
                mkdir(plan.newRunsDir)
                mkdir(plan.newBuildsDir)

                // Copy source → main.<ext>
                const sourceContent = await readFile(sourcePath)
                if (sourceContent !== null) {
                    writeFile(plan.newSourcePath, sourceContent)
                }

                // Copy config → job.json
                const configContent = await readFile(oldSettings)
                if (configContent !== null) {
                    writeFile(plan.newSettingsPath, configContent)
                } else {
                    // Create default config if none existed
                    writeFile(plan.newSettingsPath, `${serializeComponentJobConfig({ entry: `./main${plan.newSourcePath.substring(plan.newSourcePath.lastIndexOf('.'))}` })}\n`)
                }

                // Copy run history
                const runFiles = files.filter(f =>
                    (f.path === oldRunsDir || f.path.startsWith(oldRunsDir + '/')) &&
                    f.kind === 'file'
                )
                if (runFiles.length > 0) {
                    for (const rf of runFiles) {
                        const relPath = rf.path.substring(oldRunsDir.length + 1)
                        const newRunPath = `${plan.newRunsDir}/${relPath}`
                        // Ensure subdirectory exists
                        const parts = newRunPath.split('/')
                        for (let i = plan.newRunsDir.split('/').length + 1; i < parts.length; i++) {
                            mkdir(parts.slice(0, i).join('/'))
                        }
                        const content = await readFile(rf.path)
                        if (content !== null) writeFile(newRunPath, content)
                    }
                }

                // Copy build artifacts
                const buildFiles = files.filter(f =>
                    (f.path === oldBuildsDir || f.path.startsWith(oldBuildsDir + '/')) &&
                    f.kind === 'file'
                )
                if (buildFiles.length > 0) {
                    for (const bf of buildFiles) {
                        const relPath = bf.path.substring(oldBuildsDir.length + 1)
                        const newBuildPath = `${plan.newBuildsDir}/${relPath}`
                        const parts = newBuildPath.split('/')
                        for (let i = plan.newBuildsDir.split('/').length + 1; i < parts.length; i++) {
                            mkdir(parts.slice(0, i).join('/'))
                        }
                        const content = await readFile(bf.path)
                        if (content !== null) writeFile(newBuildPath, content)
                    }
                }

                // Delete old files: source, config, runs, builds
                deleteFile(sourcePath)
                if (files.some(f => f.path === oldSettings)) deleteFile(oldSettings)
                const oldRunsFiles = files.filter(f => f.path === oldRunsDir || f.path.startsWith(oldRunsDir + '/'))
                for (const f of oldRunsFiles) deleteFile(f.path)
                const oldBuildsFiles = files.filter(f => f.path === oldBuildsDir || f.path.startsWith(oldBuildsDir + '/'))
                for (const f of oldBuildsFiles) deleteFile(f.path)

                // Navigate to new job folder
                setExpandedDirs(prev => new Set([...prev, plan.jobRootPath]))
                if (onOpenComponentJob) onOpenComponentJob(plan.jobRootPath)
                else onSelect(plan.jobRootPath)
                toast(`Converted "${sourceName}" to job folder`, { type: 'success' })
                    } catch (err) {
                        toast(`Conversion failed: ${err instanceof Error ? err.message : 'unknown error'}`, { type: 'error' })
                    }
                },
            })
            return
        }
        else if (action === 'paste') {
            handlePasteFromClipboard(targetPath)
        }
    }

    // ── Batch import helper (shared by drag-drop + file picker) ──
    const executeImport = useCallback(async (entries: ImportFileEntry[]) => {
        if (!workspace.fs.importFiles || entries.length === 0) return

        const showProgress = entries.length >= 2
        const controller = new AbortController()
        abortControllerRef.current = controller

        if (showProgress) {
            setUploadProgress({ current: 0, total: entries.length, currentName: '', replaced: 0, phase: 'encrypting' })
        }

        try {
            const result = await workspace.fs.importFiles(entries, {
                signal: controller.signal,
                onProgress: ({ current, total, path, replacedTotal }) => {
                    if (showProgress) {
                        setUploadProgress(prev => prev ? {
                            ...prev,
                            current, total,
                            currentName: path.split('/').pop()!,
                            replaced: replacedTotal,
                        } : null)
                    }
                },
                onPhase: (phase) => {
                    if (showProgress) {
                        setUploadProgress(prev => prev ? {
                            ...prev,
                            phase: phase === 'committing' ? 'saving' : 'encrypting',
                            currentName: phase === 'committing'
                                ? `${prev.current} filer`
                                : prev.currentName,
                        } : null)
                    }
                },
            })

            if (result.paths.length > 0) {
                onSelect(result.paths[result.paths.length - 1])
            }
            if (result.replaced > 0) {
                toast(`Ersatte ${result.replaced} befintlig${result.replaced > 1 ? 'a' : ''} fil${result.replaced > 1 ? 'er' : ''}`, { type: 'info' })
            }
            const fileCount = result.created
            if (fileCount > 0) {
                const verb = result.aborted ? 'Importerade (delvis)' : 'Importerade'
                const msg = fileCount === 1
                    ? `${verb} ${result.paths[0].split('/').pop()}`
                    : `${verb} ${fileCount} filer`
                toast(msg, { type: result.aborted ? 'info' : 'success' })
            }
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                toast('Import avbruten — inga filer sparades', { type: 'info' })
            } else {
                toast(`Import misslyckades: ${err instanceof Error ? err.message : 'Okänt fel'}`, { type: 'error' })
            }
        } finally {
            if (showProgress) setUploadProgress(null)
            abortControllerRef.current = null
        }
    }, [workspace, onSelect, toast])

    // ── Drag & Drop ───────────────────────────────────────────────

    const handleDragOver = (path: string) => {
        setDropTargetPath(path)
        if (!expandedDirs.has(path)) {
            if (autoExpandTimer.current) clearTimeout(autoExpandTimer.current)
            autoExpandTimer.current = setTimeout(() => {
                setExpandedDirs(prev => new Set([...prev, path]))
            }, 500)
        }
    }

    const handleDrop = async (e: DragEvent<HTMLButtonElement | HTMLElement>, targetDir: string) => {
        e.preventDefault()
        e.stopPropagation()
        setDropTargetPath(null)
        setIsDraggingOver(false)
        if (autoExpandTimer.current) clearTimeout(autoExpandTimer.current)
        dragCounter.current = 0

        if (!canWrite) {
            toast('Workspace är skrivskyddad', { type: 'error' })
            return
        }

        const srcPath = e.dataTransfer.getData('application/x-secureblob-path')
        if (srcPath) {
            // Cycle detection: prevent moving folder into itself or a descendant
            if (targetDir === srcPath || targetDir.startsWith(srcPath + '/')) {
                toast('Kan inte flytta en mapp inuti sig själv', { type: 'error' })
                return
            }
            const fileName = srcPath.split('/').pop()!
            const newPath = targetDir === '/' ? `/${fileName}` : `${targetDir}/${fileName}`

            if (srcPath === newPath) return
            if (files.some(f => f.path === newPath)) {
                toast(`File "${fileName}" already exists in target`, { type: 'error' })
                return
            }

            applyCascadeRename(srcPath, newPath, files)
            toast(`Moved to ${targetDir === '/' ? 'root' : targetDir.split('/').pop()}`, {
                type: 'info',
                action: { label: 'Undo', onClick: () => applyCascadeRename(newPath, srcPath, workspace.fs.list()) }
            })
            return
        }

        const isMulti = e.dataTransfer.getData('application/x-secureblob-multiselect')
        if (isMulti) {
            const movedCount = multiSelect.size
            let fileSnapshot = files
            const moveRoots = collapseNestedMoveSelections(multiSelect)

            for (const p of moveRoots) {
                // Cycle guard: skip folders being moved into themselves
                if (targetDir === p || targetDir.startsWith(p + '/')) continue
                const fileName = p.split('/').pop()!
                const newPath = targetDir === '/' ? `/${fileName}` : `${targetDir}/${fileName}`
                if (p === newPath) continue
                if (fileSnapshot.some(f => f.path === newPath)) {
                    toast(`File "${fileName}" already exists in target`, { type: 'error' })
                    continue
                }

                const renames = applyCascadeRename(p, newPath, fileSnapshot)
                fileSnapshot = applyRenameOpsToSnapshot(fileSnapshot, renames)
            }
            setMultiSelect(new Set())
            toast(`Moved ${movedCount} items`, { type: 'info' })
            return
        }

        // ── External files from OS (files + folders) ─────────────────
        const items = e.dataTransfer.items
        if (!items || items.length === 0) return

        const prefix = targetDir === '/' ? '' : targetDir

        // Show progress modal IMMEDIATELY — before scanning starts
        const controller = new AbortController()
        abortControllerRef.current = controller
        setUploadProgress({ current: 0, total: 0, currentName: 'Skannar filer…', replaced: 0 })

        // Collect entries with EAGER content reading — webkitGetAsEntry for
        // folder structure, fallback to getAsFile.  Content is read immediately
        // (not deferred to a second loop) to prevent Safari from recycling
        // the File backing storage during mass drag-and-drop.
        const importEntries: ImportFileEntry[] = []
        for (const item of Array.from(items)) {
            if (controller.signal.aborted) break
            const entry = item.webkitGetAsEntry?.()
            if (entry) {
                const entryResults = await readEntryRecursive(entry, prefix)
                importEntries.push(...entryResults)
            } else if (item.kind === 'file') {
                const f = item.getAsFile()
                if (f) {
                    try {
                        const content = new Uint8Array(await f.arrayBuffer())
                        importEntries.push({ path: `${prefix}/${f.name}`, content, mime: f.type || undefined })
                    } catch {
                        toast(`Kunde inte läsa "${f.name}"`, { type: 'error' })
                    }
                }
            }
            // Update scanning progress
            setUploadProgress(prev => prev ? { ...prev, current: importEntries.length, total: importEntries.length } : null)
        }
        if (importEntries.length === 0 || controller.signal.aborted) {
            setUploadProgress(null)
            abortControllerRef.current = null
            return
        }

        // Reset progress — executeImport will manage its own progress from here
        setUploadProgress(null)
        abortControllerRef.current = null
        await executeImport(importEntries)
        // Phase bridge toast: import is done, sync will start automatically
        if (importEntries.length > 0) {
            toast(`Import klar — synkar till server…`, { type: 'info' })
        }
    }

    // ── Render ─────────────────────────────────────────────────────

    const isEmpty = Object.keys(tree.children).length === 0 && tree.files.length === 0
    const rootError = dirErrors['/']
    const jobInternalsTitle = showInternals ? 'Hide job internals' : 'Show job internals'
    const renderHeaderActionButtons = () => (
        <>
            {canWrite && <button className="header-btn" title="New File" onClick={() => setInlineInput({ mode: 'newFile', parentPath: '/' })}><FilePlus size={16} /></button>}
            {canWrite && <button className="header-btn" title="New Folder" onClick={() => setInlineInput({ mode: 'newFolder', parentPath: '/' })}><FolderPlus size={16} /></button>}
            {canWrite && <button className="header-btn" title="New Job" onClick={() => setInlineInput({ mode: 'newJob', parentPath: '/' })}><span style={{ fontSize: '16px' }}>🧩</span></button>}
            {hasFolderFirstJobs && <button className={`header-btn${showInternals ? ' active' : ''}`} title={jobInternalsTitle} onClick={() => setShowInternals(prev => !prev)}>{showInternals ? <EyeOff size={16} /> : <Eye size={16} />}</button>}
        </>
    )

    return (
        <div
            className={`file-browser ${isDraggingOver ? 'dragging-over' : ''}`}
            onContextMenu={(e) => {
                e.preventDefault()
                showCtxMenu(e.clientX, e.clientY, 'empty', '/')
            }}
            onDragEnter={(e) => {
                e.preventDefault(); e.stopPropagation()
                dragCounter.current++
                setIsDraggingOver(true)
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
            onDragLeave={(e) => {
                e.preventDefault(); e.stopPropagation()
                dragCounter.current--
                if (dragCounter.current === 0) setIsDraggingOver(false)
            }}
            onDrop={(e) => handleDrop(e, '/')}
        >
            {isDraggingOver && (
                <div className="drop-overlay">
                    <Upload size={28} />
                    {canWrite ? 'Släpp filer här' : 'Skrivskyddad workspace'}
                </div>
            )}
            {multiSelect.size > 0 ? (
                <div className="file-browser-header selection-bar">
                    <span className="selection-count">{multiSelect.size} selected</span>
                    <div className="file-browser-header-actions">
                        <button className="header-btn" onClick={() => { setMultiSelect(new Set()); setSelectionMode(false) }}>
                            <X size={16} />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="file-browser-header">
                    <span className="file-browser-title">FILER</span>
                    <div className="fb-search">
                        <Search size={14} className="fb-search-icon" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Sök filer..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="fb-search-input"
                        />
                        {searchQuery && (
                            <button
                                className="fb-search-clear"
                                onClick={() => setSearchQuery('')}
                                aria-label="Rensa sök"
                            >
                                <X size={14} />
                            </button>
                        )}
                        {searchQuery && searchMatchCount > 0 && (
                            <span className="fb-search-count">{searchMatchCount} träff{searchMatchCount !== 1 ? 'ar' : ''}</span>
                        )}
                        {searchQuery && searchMatchCount === 0 && matchingDirs.size === 0 && (
                            <span className="fb-search-count fb-search-count--zero">0 träffar</span>
                        )}
                    </div>
                    <div className="file-browser-header-actions">
                        {renderHeaderActionButtons()}
                    </div>
                    <div className="fb-mobile-actions">
                        <div className="fb-mobile-actions-row">
                            {renderHeaderActionButtons()}
                        </div>
                    </div>
                </div>
            )}

            <div className="file-browser-tree" tabIndex={0}>
                {isLoadingRoot && isEmpty && !inlineInput ? (
                    <div className="file-browser-loading content-appear">
                        <div className="file-browser-loading-spinner" />
                        <div className="empty-message">Laddar filer</div>
                        <div className="empty-subtitle">Hämtar filträdet för den här arbetsytan.</div>
                    </div>
                ) : rootError && isEmpty && !inlineInput ? (
                    <div className="file-browser-empty content-appear">
                        <AlertTriangle size={26} className="text-error" />
                        <div className="empty-message">Kunde inte läsa filer</div>
                        <div className="empty-subtitle">{rootError}</div>
                        <button className="btn btn-sm" onClick={() => { void reloadDir('/') }}>
                            Försök igen
                        </button>
                    </div>
                ) : isEmpty && !inlineInput ? (
                    <div className="file-browser-empty content-appear">
                        {/* Inline SVG illustration: document + sparkles */}
                        <svg className="empty-illustration" viewBox="0 0 80 80" fill="none" width="80" height="80">
                            <rect x="18" y="8" width="44" height="56" rx="6" fill="var(--color-surface-hover)" stroke="var(--color-border)" strokeWidth="1.5" />
                            <rect x="26" y="22" width="28" height="3" rx="1.5" fill="var(--color-text-tertiary)" opacity="0.5" />
                            <rect x="26" y="30" width="22" height="3" rx="1.5" fill="var(--color-text-tertiary)" opacity="0.4" />
                            <rect x="26" y="38" width="25" height="3" rx="1.5" fill="var(--color-text-tertiary)" opacity="0.3" />
                            {/* Sparkle top-right */}
                            <circle cx="58" cy="14" r="2.5" fill="var(--color-accent)" opacity="0.8">
                                <animate attributeName="opacity" values="0.8;0.3;0.8" dur="2s" repeatCount="indefinite" />
                            </circle>
                            {/* Sparkle bottom-left */}
                            <circle cx="14" cy="52" r="2" fill="var(--color-accent)" opacity="0.5">
                                <animate attributeName="opacity" values="0.5;0.2;0.5" dur="2.5s" repeatCount="indefinite" />
                            </circle>
                            {/* Sparkle mid-right */}
                            <circle cx="66" cy="36" r="1.5" fill="var(--color-accent)" opacity="0.6">
                                <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1.8s" repeatCount="indefinite" />
                            </circle>
                        </svg>

                        <div className="empty-message">Inga filer</div>
                        <div className="empty-subtitle">Din krypterade arbetsyta. Allt sparas lokalt och synkas automatiskt.</div>

                        {canWrite && (
                            <div className="empty-actions">
                                <button className="btn btn-sm" onClick={() => setInlineInput({ mode: 'newFile', parentPath: '/' })}>
                                    <FilePlus size={14} /> Ny fil
                                </button>
                                <button className="btn btn-sm" onClick={() => setInlineInput({ mode: 'newFolder', parentPath: '/' })}>
                                    <FolderPlus size={14} /> Ny mapp
                                </button>
                                <button className="btn btn-sm" onClick={() => setInlineInput({ mode: 'newJob', parentPath: '/' })}>
                                    <span style={{ fontSize: '14px' }}>🧩</span> Nytt jobb
                                </button>
                                <button className="btn btn-sm" onClick={() => {
                                    const input = document.createElement('input')
                                    input.type = 'file'
                                    input.multiple = true
                                    input.onchange = async () => {
                                        if (!input.files) return
                                        const fileList = Array.from(input.files)
                                        const entries: ImportFileEntry[] = []
                                        for (const f of fileList) {
                                            try {
                                                // Always read as raw bytes to preserve original encoding
                                                const content = new Uint8Array(await f.arrayBuffer())
                                                entries.push({ path: `/${f.name}`, content, mime: f.type || undefined })
                                            } catch {
                                                toast(`Kunde inte läsa "${f.name}"`, { type: 'error' })
                                            }
                                        }
                                        await executeImport(entries)
                                    }
                                    input.click()
                                }}>
                                    <Upload size={14} /> Ladda upp
                                </button>
                                <button className="btn btn-sm" onClick={() => handlePasteFromClipboard('/')}>
                                    <ClipboardPaste size={14} /> Klistra in
                                </button>
                            </div>
                        )}

                        <div className="empty-drag-hint">Dra filer hit</div>
                    </div>
                ) : (
                        <DirNode
                            name=""
                            node={tree}
                            depth={0}
                            selectedPath={selectedPath}
                            activeAgentPath={activeAgentPath}
                            activeTranscriptPath={activeTranscriptPath}
                            onSelect={onSelect}
                            onOpenComponentJob={onOpenComponentJob}
                            resolveJobSourcePath={resolveJobSourcePath}
                            showInternals={showInternals}
                        onContextMenu={showCtxMenu}
                        dropTargetPath={dropTargetPath}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragLeave={(e) => {
                            e.preventDefault(); e.stopPropagation()
                            if (autoExpandTimer.current) clearTimeout(autoExpandTimer.current)
                            setDropTargetPath(null)
                        }}
                        canWrite={canWrite}
                        inlineInput={inlineInput}
                        onInlineConfirm={onInlineConfirm}
                        onInlineCancel={onInlineCancel}
                        multiSelect={multiSelect}
                        selectionMode={selectionMode}
                        onToggleSelect={onToggleSelect}
                        expandedDirs={expandedDirs}
                        toggleDir={toggleDir}
                        workspace={workspace}
                        showWriterDots={showWriterDots}
                        searchQuery={searchQuery}
                        matchingDirs={matchingDirs}
                        loadingDirs={loadingDirs}
                        loadedDirs={loadedDirs}
                        dirErrors={dirErrors}
                        onRetryDir={(path) => { void reloadDir(path) }}
                    />
                )}
            </div>

            {ctxMenu && (
                <ContextMenu
                    x={ctxMenu.x}
                    y={ctxMenu.y}
                    targetType={ctxMenu.targetType}
                    targetPath={ctxMenu.targetPath}
                    sections={contextMenuSections || []}
                    onClose={hideCtxMenu}
                    onAction={handleCtxAction}
                />
            )}

            {uploadProgress && (
                <div className="upload-progress-overlay">
                    <div className="upload-progress-box">
                        <div className="upload-title">
                            {uploadProgress.phase === 'saving' ? 'Sparar…' : 'Importerar…'}
                        </div>
                        <div className="upload-bar">
                            <div
                                className={`upload-bar-fill${uploadProgress.phase === 'saving' ? ' indeterminate' : ''}`}
                                style={uploadProgress.phase !== 'saving' ? { width: `${(uploadProgress.current / uploadProgress.total) * 100}%` } : undefined}
                            />
                        </div>
                        <div className="upload-detail">
                            {uploadProgress.phase === 'saving'
                                ? `Skapar commit för ${uploadProgress.currentName}…`
                                : `${uploadProgress.currentName} (${uploadProgress.current}/${uploadProgress.total})`
                            }
                        </div>
                        {uploadProgress.replaced > 0 && (
                            <div className="upload-replaced-hint">
                                ⚠ {uploadProgress.replaced} fil{uploadProgress.replaced > 1 ? 'er' : ''} ersätts
                            </div>
                        )}
                        {uploadProgress.phase === 'saving' ? (
                            <div className="upload-saving-hint">Släpp inte fönstret — sparar till disk</div>
                        ) : (
                            <div className="upload-cancel-actions">
                                <button
                                    className="btn btn-sm btn-cancel-discard"
                                    disabled={!!uploadProgress.cancelling}
                                    onClick={() => {
                                        setUploadProgress(prev => prev ? { ...prev, cancelling: 'discard' } : prev)
                                        abortControllerRef.current?.abort('discard')
                                    }}
                                >
                                    {uploadProgress.cancelling === 'discard' ? 'Avbryter…' : 'Avbryt'}
                                </button>
                                {uploadProgress.current > 0 && (
                                    <button
                                        className="btn btn-sm btn-cancel-keep"
                                        disabled={!!uploadProgress.cancelling}
                                        onClick={() => {
                                            setUploadProgress(prev => prev ? { ...prev, cancelling: 'keep' } : prev)
                                            abortControllerRef.current?.abort('keep')
                                        }}
                                    >
                                        {uploadProgress.cancelling === 'keep'
                                            ? 'Sparar…'
                                            : `Spara ${uploadProgress.current} och avbryt`}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {pendingConfirm && (
                <ConfirmModal
                    state={pendingConfirm}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}
        </div>
    )
}
